import React, { useMemo } from 'react'
import s from './styles/Switch.module.scss'

/**
 * Universal Switch component with 2 or 3 positions
 * @param {Array<string|{value: string, label: string}>} options - Array of option labels or objects (e.g., ['ON', 'OFF'] or [{value: 'ON', label: 'ON'}])
 * @param {string} value - Current selected value
 * @param {Function} onChange - Callback when value changes: (newValue) => void
 * @param {string} className - Additional CSS class
 */
const Switch = ({
	options = ['ON', 'OFF'],
	value,
	onChange,
	className = '',
}) => {
	// Normalize options to always have {value, label} format
	const normalizedOptions = useMemo(() => {
		return options.map(opt => {
			if (typeof opt === 'string') {
				return { value: opt, label: opt }
			}
			if (opt && typeof opt === 'object' && opt.value) {
				return { value: opt.value, label: opt.label || opt.value }
			}
			return { value: String(opt), label: String(opt) }
		})
	}, [options])

	const currentIndex = useMemo(() => {
		return normalizedOptions.findIndex(opt => opt.value === value) ?? 0
	}, [normalizedOptions, value])

	const handleClick = optionValue => {
		if (onChange && optionValue !== value) {
			onChange(optionValue)
		}
	}

	const sliderStyle = useMemo(() => {
		if (currentIndex < 0) return {}
		const width = 100 / normalizedOptions.length
		return {
			width: `${width}%`,
			left: `${(currentIndex * 100) / normalizedOptions.length}%`,
		}
	}, [currentIndex, normalizedOptions.length])

	return (
		<div className={`${s.switch} ${className}`.trim()}>
			<div className={s.track}>
				<div className={s.slider} style={sliderStyle} />
				{normalizedOptions.map((option, index) => (
					<button
						key={option.value || index}
						type="button"
						className={`${s.option} ${
							value === option.value ? s.active : ''
						}`.trim()}
						onClick={() => handleClick(option.value)}
						aria-pressed={value === option.value}
					>
						{option.label}
					</button>
				))}
			</div>
		</div>
	)
}

export default Switch
